/* Kalmix custom code | File: assets/gt03-overall-timeline.js | Version: 1024.5 */
(() => {
  if (window.KalmixGt03OverallTimeline) return;
  window.KalmixGt03OverallTimeline = true;

  const ORDER = ['GUIDE-K35E-A', 'GUIDE-K35E-B', 'GUIDE-K35E-C'];
  const COLORS = ['#22a95a', '#0e5cb6', '#ec7427'];
  const GRID = '#e5ebf0';
  const TEXT = '#1f2933';
  const MUTED = '#61707d';

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function formatSeconds(value) {
    const seconds = Math.max(0, Math.round(Number(value) || 0));
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainder = seconds % 60;
    return [hours, minutes, remainder].map((part) => String(part).padStart(2, '0')).join(':');
  }

  function formatMeters(value) {
    const numeric = optionalNumber(value);
    return Number.isFinite(numeric) ? numeric.toFixed(3) + ' m' : '—';
  }

  function numericRange(values, fallback) {
    const usable = values.filter(Number.isFinite);
    if (!usable.length) return fallback;
    const minimum = Math.min(...usable);
    const maximum = Math.max(...usable);
    const padding = Math.max((maximum - minimum) * 0.08, 0.1);
    return {
      min: Math.min(fallback.min, minimum - padding),
      max: Math.max(fallback.max, maximum + padding)
    };
  }

  function getAxis(data, key, values, fallback) {
    const supplied = data.axes?.[key];
    const min = Number(supplied?.min);
    const max = Number(supplied?.max);
    if (Number.isFinite(min) && Number.isFinite(max) && max > min) return { min, max };
    return numericRange(values, fallback);
  }

  function optionalNumber(value) {
    return value === null || value === '' || typeof value === 'undefined' ? Number.NaN : Number(value);
  }

  function normalizeScenarios(source, start, end) {
    if (!Array.isArray(source)) return [];
    return source.map((scenario) => {
      const from = Number(scenario?.start_s);
      const to = Number(scenario?.end_s);
      if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;
      return {
        id: String(scenario?.id || scenario?.code || 'Scenario'),
        start: Math.max(start, from),
        end: Math.min(end, to)
      };
    }).filter((scenario) => scenario && scenario.end > scenario.start);
  }

  function validate(data) {
    if (!data || typeof data !== 'object' || !data.samples || typeof data.samples !== 'object') {
      throw new Error('Timeline data does not contain a samples object.');
    }

    const samples = ORDER.map((name, index) => {
      const points = data.samples[name]?.points;
      if (!Array.isArray(points) || !points.length) return null;
      const usable = points
        .map((point) => [optionalNumber(point?.[0]), optionalNumber(point?.[1]), optionalNumber(point?.[2])])
        .filter((point) => Number.isFinite(point[0]));
      return usable.length ? { name, color: COLORS[index], points: usable } : null;
    }).filter(Boolean);

    if (!samples.length) throw new Error('Timeline data has no usable sample points.');

    const elapsed = samples.flatMap((sample) => sample.points.map((point) => point[0]));
    const horizontal = samples.flatMap((sample) => sample.points.map((point) => point[1]));
    const vertical = samples.flatMap((sample) => sample.points.map((point) => point[2]));
    const start = Number(data.window?.start_elapsed_s);
    const end = Number(data.window?.end_elapsed_s);

    const chartStart = Number.isFinite(start) ? start : Math.min(...elapsed);
    const chartEnd = Number.isFinite(end) ? end : Math.max(...elapsed);

    return {
      samples,
      start: chartStart,
      end: chartEnd,
      scenarios: normalizeScenarios(data.scenarios, chartStart, chartEnd),
      horizontalAxis: getAxis(data, 'horizontal_error_m', horizontal, { min: 0, max: 1 }),
      verticalAxis: getAxis(data, 'vertical_error_m', vertical, { min: -1, max: 1 })
    };
  }

  function nearestPoint(points, elapsed) {
    let low = 0;
    let high = points.length - 1;
    while (low <= high) {
      const middle = Math.floor((low + high) / 2);
      if (points[middle][0] < elapsed) low = middle + 1;
      else high = middle - 1;
    }
    const candidates = [points[Math.max(0, high)], points[Math.min(points.length - 1, low)]].filter(Boolean);
    return candidates.reduce((nearest, point) => (!nearest || Math.abs(point[0] - elapsed) < Math.abs(nearest[0] - elapsed) ? point : nearest), null);
  }

  function createChart(root, chart) {
    const canvas = root.querySelector('[data-gt-v2-timeline-canvas]');
    const stage = root.querySelector('[data-gt-v2-timeline-stage]');
    const tooltip = root.querySelector('[data-gt-v2-timeline-tooltip]');
    const loading = root.querySelector('[data-gt-v2-timeline-loading]');
    const error = root.querySelector('[data-gt-v2-timeline-error]');
    const context = canvas.getContext('2d');
    let dimensions;
    let pendingFrame = null;
    let pendingHover = null;

    function scheduleRender(hoverElapsed = null) {
      pendingHover = hoverElapsed;
      if (pendingFrame !== null) return;
      pendingFrame = requestAnimationFrame(() => {
        pendingFrame = null;
        render(pendingHover);
      });
    }

    function render(hoverElapsed = null) {
      const rect = canvas.getBoundingClientRect();
      const width = rect.width;
      if (width <= 0) return;
      const height = Math.max(420, rect.height);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const mobile = width < 640;
      const left = mobile ? 56 : 68;
      const right = 24;
      const top = mobile ? 36 : 42;
      const bottom = 42;
      const panelGap = 54;
      const panelHeight = (height - top - bottom - panelGap) / 2;
      const horizontalPanel = { left, top, width: width - left - right, height: panelHeight };
      const verticalPanel = { left, top: top + panelHeight + panelGap, width: width - left - right, height: panelHeight };
      const duration = Math.max(1, chart.end - chart.start);
      const mapX = (elapsed) => horizontalPanel.left + ((elapsed - chart.start) / duration) * horizontalPanel.width;
      const mapY = (value, panel, axis) => panel.top + panel.height - ((value - axis.min) / (axis.max - axis.min)) * panel.height;

      const bitmapWidth = Math.round(width * dpr);
      const bitmapHeight = Math.round(height * dpr);
      if (canvas.width !== bitmapWidth) canvas.width = bitmapWidth;
      if (canvas.height !== bitmapHeight) canvas.height = bitmapHeight;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      context.clearRect(0, 0, width, height);
      context.fillStyle = '#fff';
      context.fillRect(0, 0, width, height);
      context.font = '600 11px Archivo, Arial, sans-serif';

      function drawScenarioBands() {
        const bandTop = horizontalPanel.top;
        const bandHeight = verticalPanel.top + verticalPanel.height - bandTop;

        chart.scenarios.forEach((scenario) => {
          const startX = mapX(scenario.start);
          const endX = mapX(scenario.end);
          const bandWidth = Math.max(1, endX - startX);
          const aliases={'Tunnel outbound':'Tunnel','Tunnel return':'Tunnel','Overhead Obstruction':'Overhead'};
          const label=(aliases[scenario.id]||scenario.id).toUpperCase();

          context.fillStyle = 'rgba(14, 92, 182, 0.055)';
          context.fillRect(startX, bandTop, bandWidth, bandHeight);

          context.fillStyle = '#0e5cb6';
          context.font = '800 10px Archivo, Arial, sans-serif';
          context.textBaseline = 'top';
          context.textAlign = 'center';
          context.fillText(label, (startX + endX) / 2, horizontalPanel.top + 7);
        });
      }

      function drawPanel(panel, axis, label) {
        context.fillStyle = TEXT;
        context.textAlign = 'left';
        context.textBaseline = 'bottom';
        context.font = '700 12px Archivo, Arial, sans-serif';
        context.fillText(label, panel.left, panel.top - 8);
        context.font = '600 11px Archivo, Arial, sans-serif';
        for (let index = 0; index <= 4; index += 1) {
          const value = axis.min + ((axis.max - axis.min) * index / 4);
          const y = mapY(value, panel, axis);
          context.strokeStyle = GRID;
          context.lineWidth = 1;
          context.beginPath();
          context.moveTo(panel.left, y);
          context.lineTo(panel.left + panel.width, y);
          context.stroke();
          context.fillStyle = MUTED;
          context.textAlign = 'right';
          context.textBaseline = 'middle';
          context.fillText(value.toFixed(Math.abs(value) < 1 ? 2 : 1), panel.left - 9, y);
        }
      }

      drawScenarioBands();
      drawPanel(horizontalPanel, chart.horizontalAxis, 'Horizontal error (m)');
      drawPanel(verticalPanel, chart.verticalAxis, 'Vertical error (m)');

      const tickCount = mobile ? 3 : 5;
      for (let index = 0; index <= tickCount; index += 1) {
        const elapsed = chart.start + (duration * index / tickCount);
        const x = mapX(elapsed);
        [horizontalPanel, verticalPanel].forEach((panel) => {
          context.strokeStyle = GRID;
          context.beginPath();
          context.moveTo(x, panel.top);
          context.lineTo(x, panel.top + panel.height);
          context.stroke();
        });
        context.fillStyle = MUTED;
        context.textAlign = index === 0 ? 'left' : index === tickCount ? 'right' : 'center';
        context.textBaseline = 'top';
        context.fillText(formatSeconds(elapsed - chart.start), x, verticalPanel.top + verticalPanel.height + 11);
      }

      function drawSeries(panel, axis, valueIndex) {
        chart.samples.forEach((sample) => {
          context.save();
          context.beginPath();
          context.rect(panel.left, panel.top, panel.width, panel.height);
          context.clip();
          context.beginPath();
          context.strokeStyle = sample.color;
          context.lineWidth = 1.35;
          context.lineJoin = 'round';
          let open = false;
          sample.points.forEach((point) => {
            const value = point[valueIndex];
            if (!Number.isFinite(value)) {
              open = false;
              return;
            }
            const x = mapX(point[0]);
            const y = mapY(value, panel, axis);
            if (!open) {
              context.moveTo(x, y);
              open = true;
            } else {
              context.lineTo(x, y);
            }
          });
          context.stroke();
          context.restore();
        });
      }

      drawSeries(horizontalPanel, chart.horizontalAxis, 1);
      drawSeries(verticalPanel, chart.verticalAxis, 2);

      if (hoverElapsed !== null) {
        const clamped = Math.max(chart.start, Math.min(chart.end, hoverElapsed));
        const x = mapX(clamped);
        [horizontalPanel, verticalPanel].forEach((panel) => {
          context.strokeStyle = 'rgba(31, 41, 51, 0.5)';
          context.beginPath();
          context.moveTo(x, panel.top);
          context.lineTo(x, panel.top + panel.height);
          context.stroke();
        });
        chart.samples.forEach((sample) => {
          const point = nearestPoint(sample.points, clamped);
          if (!point) return;
          [[horizontalPanel, chart.horizontalAxis, point[1]], [verticalPanel, chart.verticalAxis, point[2]]].forEach(([panel, axis, value]) => {
            if (!Number.isFinite(value)) return;
            context.fillStyle = sample.color;
            context.beginPath();
            context.arc(x, mapY(value, panel, axis), 3, 0, Math.PI * 2);
            context.fill();
          });
        });
      }

      dimensions = { horizontalPanel, verticalPanel, mapX, duration };
      loading.hidden = true;
      error.hidden = true;
    }

    function showTooltip(event) {
      if (!dimensions) return;
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const { horizontalPanel, duration } = dimensions;
      if (x < horizontalPanel.left || x > horizontalPanel.left + horizontalPanel.width) {
        tooltip.hidden = true;
        scheduleRender();
        return;
      }
      const elapsed = chart.start + ((x - horizontalPanel.left) / horizontalPanel.width) * duration;
      const rows = chart.samples.map((sample) => {
        const point = nearestPoint(sample.points, elapsed);
        return '<div class="gt-v2-overall__timeline-tooltip-row"><span><i style="background:' + sample.color + '"></i>' +
          escapeHtml(sample.name) + '</span><b>H</b><strong>' + escapeHtml(formatMeters(point?.[1])) + '</strong><b>V</b><strong>' + escapeHtml(formatMeters(point?.[2])) + '</strong></div>';
      }).join('');
      tooltip.innerHTML = '<div class="gt-v2-overall__timeline-tooltip-title">Elapsed ' + escapeHtml(formatSeconds(elapsed - chart.start)) + '</div>' + rows;
      tooltip.hidden = false;
      const tooltipWidth = tooltip.offsetWidth || 360;
      const tooltipHeight = tooltip.offsetHeight || 160;
      const localY = event.clientY - stage.getBoundingClientRect().top;
      const preferredLeft = x + 16;
      const left = preferredLeft + tooltipWidth <= stage.clientWidth - 10
        ? preferredLeft
        : x - tooltipWidth - 16;
      const top = Math.max(10, Math.min(localY - 12, stage.clientHeight - tooltipHeight - 10));
      tooltip.style.left = Math.max(10, left) + 'px';
      tooltip.style.top = top + 'px';
      scheduleRender(elapsed);
    }

    canvas.addEventListener('pointermove', showTooltip);
    canvas.addEventListener('pointerdown', showTooltip);
    function hideTooltip() {
      tooltip.hidden = true;
      scheduleRender();
    }
    canvas.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'touch') hideTooltip();
    });
    canvas.addEventListener('pointercancel', hideTooltip);

    const observer = new ResizeObserver(() => render());
    observer.observe(canvas);
    render();
  }

  function initialize(root) {
    if (!root || root.dataset.timelineReady === 'true' || !root.dataset.timelineUrl) return;
    root.dataset.timelineReady = 'true';
    fetch(root.dataset.timelineUrl, { credentials: 'omit', cache: 'force-cache' })
      .then((response) => {
        if (!response.ok) throw new Error('Timeline data request failed (' + response.status + ').');
        return response.json();
      })
      .then((data) => createChart(root, validate(data)))
      .catch((reason) => {
        const loading = root.querySelector('[data-gt-v2-timeline-loading]');
        const error = root.querySelector('[data-gt-v2-timeline-error]');
        if (loading) loading.hidden = true;
        if (error) {
          error.hidden = false;
          error.textContent = reason.message || 'Unable to load time-series data.';
        }
      });
  }

  function initializeAll() {
    document.querySelectorAll('[data-gt-v2-timeline]').forEach(initialize);
  }

  initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
})();
